import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { AccountDetail, SystemRec } from "@/lib/types";
import { whenShort } from "@/lib/format";
import { Button, Card, Modal, Field, Dot, Empty, Toggle, toast } from "@/components/ui";
import { SystemCard } from "./SystemCard";

interface Detail extends SystemRec { events: { id: string; fromStatus: string; toStatus: string; cause: string; note: string | null; createdAt: string; byName: string | null }[] }

export function SystemsTab({ account, refetch }: { account: AccountDetail; refetch: () => void }) {
  const [editing, setEditing] = useState<SystemRec | "new" | null>(null);
  return (
    <div className="col gap-16">
      <div className="row"><Button variant="primary" className="ml-auto" onClick={() => setEditing("new")}>Add system</Button></div>
      {account.systems.length === 0 && <Card><Empty>No systems yet. Add the client app so status lights, tickets and GitHub issues can attach to it.</Empty></Card>}
      {account.systems.map((s) => (
        <div key={s.id} className="col gap-8">
          <SystemCard system={s} account={account} refetch={refetch} onEmail={() => {}} onTicket={() => {}} />
          <div className="row gap-8"><button className="btn-link small" onClick={() => setEditing(s)}>Edit system</button><StatusHistory id={s.id} /></div>
        </div>
      ))}
      {editing && <SystemModal accountId={account.id} system={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); invalidate("systems"); invalidate("accounts"); }} />}
    </div>
  );
}

function StatusHistory({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery<Detail>(open ? `system:${id}` : null, () => api.get<Detail>(`/systems/${id}`), [id]);
  return (
    <>
      <button className="btn-link small" onClick={() => setOpen(true)}>Status history</button>
      {open && (
        <Modal title="Status history" onClose={() => setOpen(false)}>
          {(data?.events ?? []).length === 0 && <span className="muted">No status changes yet.</span>}
          {(data?.events ?? []).map((e) => (
            <div key={e.id} className="row gap-10" style={{ alignItems: "flex-start" }}>
              <Dot color={e.toStatus as "red"} />
              <div className="col gap-2 grow"><span>{e.fromStatus} → {e.toStatus}{e.note ? <span className="muted"> · {e.note}</span> : ""}</span><span className="xs muted">{e.byName ?? e.cause} · {whenShort(e.createdAt)}</span></div>
            </div>
          ))}
        </Modal>
      )}
    </>
  );
}

function SystemModal({ accountId, system, onClose, onSaved }: { accountId: string; system: SystemRec | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: system?.name ?? "", demoUrl: system?.demoUrl ?? "", productionUrl: system?.productionUrl ?? "", repo: system?.repo ?? "", live: system?.live ?? false, licenseMonthly: system?.licenseMonthly ?? "", licensePaidThrough: system?.licensePaidThrough?.slice(0, 10) ?? "", lastDeployRef: system?.lastDeployRef ?? "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, licenseMonthly: f.licenseMonthly === "" ? null : Number(f.licenseMonthly), licensePaidThrough: f.licensePaidThrough || null, lastDeployRef: f.lastDeployRef || null, ...(f.lastDeployRef && f.lastDeployRef !== system?.lastDeployRef ? { lastDeployAt: new Date().toISOString() } : {}) };
      if (system) await api.patch(`/systems/${system.id}`, body); else await api.post("/systems", { ...body, accountId });
      onSaved();
    } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  return (
    <Modal title={system ? "Edit system" : "Add system"} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !f.name.trim()} onClick={save}>Save</Button></>}>
      <Field label="Name"><input className="input" autoFocus placeholder="Shop Floor Scoreboard" value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} /></Field>
      <div className="form-grid">
        <Field label="Production URL"><input className="input" placeholder="https://floor.client.com" value={f.productionUrl} onChange={(e) => setF((x) => ({ ...x, productionUrl: e.target.value }))} /></Field>
        <Field label="Demo URL"><input className="input" value={f.demoUrl} onChange={(e) => setF((x) => ({ ...x, demoUrl: e.target.value }))} /></Field>
        <Field label="GitHub repo" hint="owner/name. Tickets open issues here; closing the issue closes the ticket."><input className="input" placeholder="hixonops/client-app" value={f.repo} onChange={(e) => setF((x) => ({ ...x, repo: e.target.value }))} /></Field>
        <Field label="Last deploy ref"><input className="input" placeholder="main@a41c2e" value={f.lastDeployRef} onChange={(e) => setF((x) => ({ ...x, lastDeployRef: e.target.value }))} /></Field>
        <Field label="License $/month"><input className="input" type="number" value={f.licenseMonthly} onChange={(e) => setF((x) => ({ ...x, licenseMonthly: e.target.value }))} /></Field>
        <Field label="Paid through"><input className="input" type="date" value={f.licensePaidThrough} onChange={(e) => setF((x) => ({ ...x, licensePaidThrough: e.target.value }))} /></Field>
      </div>
      <div className="row gap-10"><Toggle on={f.live} onChange={(v) => setF((x) => ({ ...x, live: v }))} label="Live" /><span>Live (in production, license billing)</span></div>
    </Modal>
  );
}
